import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { LogOut } from 'lucide-react';
import { Badge } from './ui/badge';
import { Item } from '@/types/global';
import { useAuth } from '@/hooks/use-auth';
import { getRoleConfig } from '@/lib/role-colors';

export function AppSidebar({ items }: { items: Item[] }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const getRoleBadge = () => {
    if (!user?.role) return null;

    const config = getRoleConfig(user.role);

    if (!config) {
      return (
        <Badge className='bg-gray-500 text-white ml-0'>
          {user.role}
        </Badge>
      );
    }

    return (
      <Badge className={`${config.badgeClass} ml-0`}>
        {config.label}
      </Badge>
    );
  };

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
      // Don't navigate immediately - the auth state change will trigger redirect
      // via ProtectedRoute when user becomes null
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  return (
    <Sidebar collapsible='icon' className='border-r border-gray-200 bg-white'>
      <SidebarHeader className='border-b border-gray-100'>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size='lg'
              className='hover:bg-transparent hover:text-current cursor-default pointer-events-none'
            >
              <img
                src='/smile_logo.png'
                alt='Smile Data Logo'
                className='size-8 object-contain shrink-0'
              />
              <div className='grid flex-1 text-left leading-tight'>
                <span className='truncate text-lg font-bold text-gray-900'>Smile Data</span>
                <span className='truncate text-sm text-gray-600'>Healthcare Analytics</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className='px-2 py-4 group-data-[collapsible=icon]:px-0'>
        <SidebarGroup>
          <SidebarGroupLabel className='text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2 group-data-[collapsible=icon]:hidden'>
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className='space-y-1'>
              {items.map((item) => {
                const isActive = location.pathname === item.url || location.pathname.startsWith(item.url + '/');
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      className={`hover:bg-green-600 hover:text-white transition-colors rounded-lg ${
                        isActive ? 'bg-green-600 text-white' : ''
                      }`}
                    >
                      <Link to={item.url} className='flex items-center gap-3 px-3 py-2 group-data-[collapsible=icon]:justify-center'>
                        <item.icon className='w-5 h-5' />
                        <span className='font-medium group-data-[collapsible=icon]:hidden'>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className='border-t border-gray-200 p-4'>
          <div className='flex gap-3 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2'>
            <Avatar className='h-10 w-10 flex-shrink-0 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8'>
              <AvatarImage src={user?.avatar_url || undefined} />
              <AvatarFallback className='bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-semibold group-data-[collapsible=icon]:text-xs'>
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>

            <div className='flex-1 min-w-0 group-data-[collapsible=icon]:hidden'>
              <p className='text-sm font-medium text-gray-900 truncate'>
                {user?.full_name || user?.email?.split('@')[0] || 'User'}
              </p>
              <p className='text-xs text-gray-500 truncate'>
                {user?.email || 'No email'}
              </p>
              <div className='mt-1 group-data-[collapsible=icon]:hidden'>
                {getRoleBadge()}
              </div>
            </div>
            
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className='p-2 h-fit rounded-md text-gray-400 hover:text-white hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0'
              title='Sign Out'
            >
              {isSigningOut ? (
                <svg className='h-4 w-4 animate-spin' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                </svg>
              ) : (
                <LogOut className='h-4 w-4' />
              )}
            </button>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
